import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CONTENT = {
  id: {
    tag: "Legal",
    title: "Kebijakan Privasi",
    updated: "Terakhir diperbarui: 1 Maret 2026",
    sections: [
      {
        title: "1. Data yang Kami Kumpulkan",
        items: [
          "Informasi akun: nama, alamat email, foto profil (opsional)",
          "Data penggunaan: jumlah analisis, paket berlangganan, riwayat pembayaran",
          "Hasil analisis: verdict, confidence score, ringkasan forensik (teks saja)",
          "Data teknis: alamat IP, browser, sistem operasi (untuk keamanan)",
        ],
      },
      {
        title: "2. Data yang TIDAK Kami Simpan",
        items: [
          "Video yang Anda upload — dihapus segera setelah analisis selesai (~2 menit)",
          "Password dalam bentuk plain text — hanya hash yang tersimpan",
          "Informasi kartu kredit — diproses langsung oleh Midtrans",
        ],
      },
      {
        title: "3. Penggunaan Data",
        items: [
          "Memberikan dan meningkatkan layanan analisis video",
          "Mengirim notifikasi penting (konfirmasi pembayaran, peringatan kuota)",
          "Keamanan akun dan pencegahan penyalahgunaan",
          "Statistik agregat tanpa identifikasi personal",
        ],
      },
      {
        title: "4. Berbagi Data",
        items: [
          "Kami tidak menjual data Anda kepada pihak ketiga",
          "Laporan analisis yang ditandai 'publik' dapat dilihat melalui link berbagi",
          "Data dapat dibagikan ke penyedia layanan kami (Midtrans, Google) sesuai kebutuhan operasional",
        ],
      },
      {
        title: "5. Hak Pengguna",
        items: [
          "Akses: lihat semua data Anda di halaman Profil dan Riwayat",
          "Koreksi: edit nama dan informasi profil kapan saja",
          "Hapus: minta penghapusan akun via email ke hello@veriksa.id",
          "Ekspor: minta ekspor data via email ke hello@veriksa.id",
        ],
      },
      {
        title: "6. Keamanan Data",
        items: [
          "Enkripsi data saat transit (HTTPS/TLS)",
          "Password di-hash menggunakan bcrypt",
          "Sesi autentikasi menggunakan token terenkripsi dengan masa berlaku terbatas",
        ],
      },
      {
        title: "7. Kontribusi Data Pelatihan AI (Opsional)",
        items: [
          "Anda dapat memilih opt-in untuk mengizinkan hasil analisis dipakai meningkatkan kualitas model.",
          "Kami hanya memproses data yang Anda izinkan melalui pengaturan Profil.",
          "Anda dapat melakukan opt-out kapan saja; data yang ditandai untuk pelatihan akan dikeluarkan dari pipeline training.",
        ],
      },
    ],
    contactNote: "Pertanyaan tentang privasi:",
    termsLink: "Syarat & Ketentuan →",
    aboutLink: "Tentang Kami",
  },
  en: {
    tag: "Legal",
    title: "Privacy Policy",
    updated: "Last updated: March 1, 2026",
    sections: [
      {
        title: "1. Data We Collect",
        items: [
          "Account information: name, email address, profile picture (optional)",
          "Usage data: number of analyses, subscription plan, payment history",
          "Analysis results: verdict, confidence score, forensic summary (text only)",
          "Technical data: IP address, browser, operating system (for security)",
        ],
      },
      {
        title: "2. Data We Do NOT Store",
        items: [
          "Videos you upload — deleted immediately after analysis is complete (~2 minutes)",
          "Passwords in plain text — only hashes are stored",
          "Credit card information — processed directly by Midtrans",
        ],
      },
      {
        title: "3. Data Usage",
        items: [
          "Providing and improving video analysis services",
          "Sending important notifications (payment confirmations, quota warnings)",
          "Account security and abuse prevention",
          "Aggregate statistics without personal identification",
        ],
      },
      {
        title: "4. Data Sharing",
        items: [
          "We do not sell your data to third parties",
          "Analysis reports marked as 'public' can be viewed via share links",
          "Data may be shared with our service providers (Midtrans, Google) as needed for operations",
        ],
      },
      {
        title: "5. User Rights",
        items: [
          "Access: view all your data on the Profile and History pages",
          "Correction: edit your name and profile information at any time",
          "Deletion: request account deletion via email to hello@veriksa.id",
          "Export: request data export via email to hello@veriksa.id",
        ],
      },
      {
        title: "6. Data Security",
        items: [
          "Data encryption in transit (HTTPS/TLS)",
          "Passwords hashed using bcrypt",
          "Authentication sessions using encrypted tokens with limited expiry",
        ],
      },
      {
        title: "7. AI Training Data Contribution (Optional)",
        items: [
          "You can opt in to allow analysis outputs to be used for model-quality improvement.",
          "We only process data that you explicitly allow through Profile settings.",
          "You can opt out anytime; data marked for training will be removed from the training pipeline.",
        ],
      },
    ],
    contactNote: "Privacy questions:",
    termsLink: "Terms & Conditions →",
    aboutLink: "About Us",
  },
};

export default function Privacy() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] === "en" ? "en" : "id";
  const c = CONTENT[lang];

  return (
    <main className="min-h-screen" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{c.tag}</span>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2 mb-2">{c.title}</h1>
          <p className="text-xs text-muted-foreground mb-10 font-mono">{c.updated}</p>

          <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">
            {c.sections.map(({ title, items }) => (
              <div key={title}>
                <h2 className="font-heading font-semibold text-foreground mb-3">{title}</h2>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="p-4 rounded-md border border-border bg-card">
              <p className="text-xs">{c.contactNote} <strong className="text-foreground">privacy@veriksa.id</strong></p>
            </div>
          </div>

          <div className="mt-10 flex gap-4 border-t border-border pt-6">
            <Link to="/terms" className="text-sm text-brand-primary hover:underline">{c.termsLink}</Link>
            <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground">{c.aboutLink}</Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
